import { IS_PRODUCTION } from "@app/config/getServiceUrl";

const getVapidPublicKey = () => {
  if (IS_PRODUCTION) {
    return "BP_UoNB1drQyfkL7Ttd4vAcQ5OglZygsDj-xH1QyXC79ND9IcPdnR8mU_IZb3WFt4MQnqHrT-05BH29XXbOFuyE";
  }

  return "BAXh6IejnjwUc1C6oy52bpN1GHKGXjNNaNneEmRNnYC2FHKlqUJzOCXyEXWz1XNTTMpk97K_sCpLPMf4Jqaqu8A";
};

const VAPID_PUBLIC_KEY = getVapidPublicKey();

export function isPushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export type PushPermissionState = "granted" | "denied" | "default" | "unsupported";

export function getPushPermissionState(): PushPermissionState {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission;
}

/**
 * 브라우저 푸시 구독을 생성하거나 기존 구독을 반환한다.
 * 권한이 거부되면 null을 반환한다.
 */
export async function subscribeToPush(): Promise<PushSubscriptionJSON | null> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return null;

  const registration = await getActiveRegistration();
  if (!registration) return null;

  const existing = await registration.pushManager.getSubscription();
  if (existing) return existing.toJSON();

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY).buffer as ArrayBuffer,
  });

  return subscription.toJSON();
}

/**
 * 현재 브라우저의 푸시 구독을 해제하고 해제된 endpoint를 반환한다.
 * 구독이 없으면 null을 반환한다.
 */
export async function unsubscribeFromPush(): Promise<string | null> {
  const registration = await getActiveRegistration();
  if (!registration) return null;

  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return null;

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  return endpoint;
}

export async function getCurrentSubscription(): Promise<PushSubscriptionJSON | null> {
  if (!isPushSupported()) return null;

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration?.active) return null;

    const subscription = await registration.pushManager.getSubscription();
    return subscription?.toJSON() ?? null;
  } catch {
    return null;
  }
}

/**
 * 등록된 서비스워커가 있으면 활성화를 기다려 반환한다.
 * 등록 자체가 없으면 (개발 환경 등) 즉시 null을 반환하여 hang을 방지한다.
 */
async function getActiveRegistration(): Promise<ServiceWorkerRegistration | null> {
  const existing = await navigator.serviceWorker.getRegistration();
  if (!existing) return null;

  return navigator.serviceWorker.ready;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}
